import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  CircularProgress,
  Alert,
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Collapse,
  Tooltip,
} from '@mui/material';
import PrintRounded from '@mui/icons-material/PrintRounded';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { PRINT_SERVER } from '../data/printServer';
import { useMenu } from '../state/MenuContext';

interface PrinterConfig {
  id: string;
  label: string;
  type: 'tcp' | 'system';
  host: string;
  port: number;
  name: string;
}

interface MultiPrinterConfig {
  printers: PrinterConfig[];
  defaultPrinter: string;
  receiptPrinter: string;
  categoryPrinter: Record<string, string>;
}

interface PrinterSettingsProps {
  open: boolean;
  onClose: () => void;
}

const EMPTY_PRINTER = (): PrinterConfig => ({
  id: crypto.randomUUID(),
  label: '',
  type: 'tcp',
  host: '',
  port: 9100,
  name: '',
});

const PrinterSettings: React.FC<PrinterSettingsProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { tabs } = useMenu();
  const visibleTabs = tabs.filter(tab => !tab.archived);

  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState<MultiPrinterConfig>({
    printers: [],
    defaultPrinter: '',
    receiptPrinter: '',
    categoryPrinter: {},
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<PrinterConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    setTab(0);
    setEditingId(null);
    setEditDraft(null);
    fetch(`${PRINT_SERVER}/config`)
      .then(r => r.json())
      .then(setConfig)
      .catch(() => {});
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await fetch(`${PRINT_SERVER}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!r.ok) throw new Error(t('printerSettings.saveFailed'));
      setMessage({ type: 'success', text: t('printerSettings.settingsSaved') });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (printer: PrinterConfig) => {
    setTestingId(printer.id);
    setMessage(null);
    try {
      const r = await fetch(`${PRINT_SERVER}/config/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ printerId: printer.id }),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Test failed');
      setMessage({ type: 'success', text: t('printerSettings.testTicketSent') });
    } catch (e: any) {
      setMessage({ type: 'error', text: t('printerSettings.printFailed', { message: e.message }) });
    } finally {
      setTestingId(null);
    }
  };

  const startEdit = (printer: PrinterConfig) => {
    setEditingId(printer.id);
    setEditDraft({ ...printer });
  };

  const commitEdit = () => {
    if (!editDraft) return;
    setConfig(c => ({
      ...c,
      printers: c.printers.map(p => p.id === editDraft.id ? editDraft : p),
    }));
    setEditingId(null);
    setEditDraft(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const addPrinter = () => {
    const p = EMPTY_PRINTER();
    setConfig(c => ({ ...c, printers: [...c.printers, p] }));
    setEditingId(p.id);
    setEditDraft(p);
  };

  const deletePrinter = (id: string) => {
    setConfig(c => {
      const printers = c.printers.filter(p => p.id !== id);
      const fallback = printers[0]?.id ?? '';
      const categoryPrinter = { ...c.categoryPrinter };
      for (const [catId, pId] of Object.entries(categoryPrinter)) {
        if (pId === id) delete categoryPrinter[catId];
      }
      return {
        ...c,
        printers,
        defaultPrinter: c.defaultPrinter === id ? fallback : c.defaultPrinter,
        receiptPrinter: c.receiptPrinter === id ? fallback : c.receiptPrinter,
        categoryPrinter,
      };
    });
  };

  const canDelete = (_id: string) => config.printers.length > 1;

  const printerSummary = (p: PrinterConfig) =>
    p.type === 'tcp' ? `${p.host || '—'}:${p.port}` : p.name || '—';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PrintRounded sx={{ color: 'primary.main' }} />
        {t('printerSettings.title')}
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t('printerSettings.printersTab')} />
          <Tab label={t('printerSettings.assignmentsTab')} />
        </Tabs>
      </Box>

      <DialogContent sx={{ minHeight: 320 }}>
        {/* ── Printers tab ── */}
        {tab === 0 && (
          <Box>
            <List disablePadding>
              {config.printers.map(printer => (
                <Box key={printer.id}>
                  <ListItem
                    disablePadding
                    sx={{ py: 0.5 }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                          size="small"
                          onClick={() => handleTest(printer)}
                          disabled={testingId === printer.id}
                          startIcon={testingId === printer.id ? <CircularProgress size={12} /> : null}
                        >
                          {t('printerSettings.testPrint')}
                        </Button>
                        <IconButton size="small" onClick={() => startEdit(printer)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title={!canDelete(printer.id) ? t('printerSettings.confirmDelete') : ''}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => deletePrinter(printer.id)}
                              disabled={!canDelete(printer.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={printer.label || <em style={{ color: '#999' }}>Unnamed</em>}
                      secondary={`${printer.type === 'tcp' ? 'LAN' : 'USB'} · ${printerSummary(printer)}`}
                    />
                  </ListItem>

                  <Collapse in={editingId === printer.id} unmountOnExit>
                    <Box sx={{ pl: 2, pr: 1, pb: 2, pt: 1, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                      <TextField
                        label={t('printerSettings.printerLabel')}
                        value={editDraft?.label ?? ''}
                        onChange={e => setEditDraft(d => d && ({ ...d, label: e.target.value }))}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.875rem' }}>
                          {t('printerSettings.connectionType')}
                        </FormLabel>
                        <RadioGroup
                          row
                          value={editDraft?.type ?? 'tcp'}
                          onChange={e => setEditDraft(d => d && ({ ...d, type: e.target.value as 'tcp' | 'system' }))}
                        >
                          <FormControlLabel value="tcp" control={<Radio size="small" />} label={t('printerSettings.lanTcp')} />
                          <FormControlLabel value="system" control={<Radio size="small" />} label={t('printerSettings.usbSystem')} />
                        </RadioGroup>
                      </FormControl>
                      {editDraft?.type === 'tcp' ? (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            label={t('printerSettings.printerIp')}
                            value={editDraft?.host ?? ''}
                            onChange={e => setEditDraft(d => d && ({ ...d, host: e.target.value }))}
                            fullWidth
                            size="small"
                            placeholder="192.168.1.100"
                          />
                          <TextField
                            label={t('printerSettings.port')}
                            value={editDraft?.port ?? 9100}
                            onChange={e => setEditDraft(d => d && ({ ...d, port: Number(e.target.value) }))}
                            sx={{ width: 90 }}
                            size="small"
                            type="number"
                          />
                        </Box>
                      ) : (
                        <TextField
                          label={t('printerSettings.printerName')}
                          value={editDraft?.name ?? ''}
                          onChange={e => setEditDraft(d => d && ({ ...d, name: e.target.value }))}
                          fullWidth
                          size="small"
                          placeholder="MUNBYN ITPP047P"
                          helperText={t('printerSettings.printerNameHelper')}
                        />
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={cancelEdit}>{t('printerSettings.cancel')}</Button>
                        <Button size="small" variant="contained" onClick={commitEdit}>{t('printerSettings.save')}</Button>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </List>

            <Button
              startIcon={<AddIcon />}
              onClick={addPrinter}
              sx={{ mt: 1 }}
            >
              {t('printerSettings.addPrinter')}
            </Button>
          </Box>
        )}

        {/* ── Assignments tab ── */}
        {tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('printerSettings.defaultPrinter')}</InputLabel>
                <Select
                  value={config.defaultPrinter}
                  label={t('printerSettings.defaultPrinter')}
                  onChange={e => setConfig(c => ({ ...c, defaultPrinter: e.target.value }))}
                >
                  {config.printers.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.label || p.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>{t('printerSettings.receiptPrinter')}</InputLabel>
                <Select
                  value={config.receiptPrinter}
                  label={t('printerSettings.receiptPrinter')}
                  onChange={e => setConfig(c => ({ ...c, receiptPrinter: e.target.value }))}
                >
                  {config.printers.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.label || p.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {visibleTabs.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {t('printerSettings.categoryAssignments')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {visibleTabs.map(menuTab => (
                    <FormControl key={menuTab.id} fullWidth size="small">
                      <InputLabel>{menuTab.name}</InputLabel>
                      <Select
                        value={config.categoryPrinter[menuTab.id] ?? ''}
                        label={menuTab.name}
                        onChange={e => setConfig(c => ({
                          ...c,
                          categoryPrinter: { ...c.categoryPrinter, [menuTab.id]: e.target.value },
                        }))}
                      >
                        <MenuItem value="">{t('printerSettings.useDefaultPrinter')}</MenuItem>
                        {config.printers.map(p => (
                          <MenuItem key={p.id} value={p.id}>{p.label || p.id}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {message && (
          <Alert severity={message.type} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>{t('printerSettings.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? t('printerSettings.saving') : t('printerSettings.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrinterSettings;
